import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";
import { Services, SERVICES_URL } from "$utils/typeConstants";
import useAuth from "$hooks/useAuth";
import { normalizeArray, getHeaders } from "$utils/utils";

const useAvailableReceptions = (
  integratedOrders: EDBIntegratedOrders,
  orderIds: number[]
) => {
  const { token } = useAuth();

  // Build query parameters
  const queryParams = useMemo(() => {
    if (orderIds.length === 0) return "";
    const params = new URLSearchParams();
    params.append("orderId", orderIds.join(","));
    return params.toString();
  }, [orderIds]);

  // Fetch receptions directly from the API
  const {
    data: receptionsResponse,
    isLoading: isFetching,
    refetch: fetchReceptions,
  } = useQuery({
    queryKey: ["receptions", "edb", "orderIds", orderIds.join(",")],

    queryFn: async () => {
      if (orderIds.length === 0) return { data: [] };

      const gatewayUrl = `${
        SERVICES_URL[Services.GATEWAY_API]
      }/api/order/receptions/edb`;
      const url = queryParams ? `${gatewayUrl}?${queryParams}` : gatewayUrl;
      const headers = getHeaders(token);

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch receptions: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    },

    enabled: orderIds.length > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Transform receptions to flatten orders structure for frontend compatibility
  // Backend returns: reception.orders = OrderReception[] (junction table records)
  // We need: reception.orders = Order[] (flattened)
  const transformedReceptions = useMemo(() => {
    if (!receptionsResponse?.data) return [];

    return normalizeArray(receptionsResponse.data).map((reception: any) => ({
      ...reception,
      orders:
        reception.orders?.map((or: any) => or.order || or).filter(Boolean) ||
        [],
      productQuantity:
        reception.productQuantities || reception.productQuantity || [],
    }));
  }, [receptionsResponse]);

  // Helper function to get receptions for a specific order
  const getOrderReceptions = useCallback(
    (orderId: number) => {
      if (!transformedReceptions) return [];
      return normalizeArray(transformedReceptions).filter((reception) =>
        normalizeArray(reception?.orders).some((o: any) => o.id === orderId)
      );
    },
    [transformedReceptions]
  );

  return {
    receptions: transformedReceptions || [],
    isFetching,
    fetchReceptions,
    getOrderReceptions,
  };
};

export default useAvailableReceptions;
